import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Inbox, CalendarClock, CheckCircle2 } from "lucide-react";
import { Logo } from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = ["partial", "new", "contacted", "booked"];

const statusStyles = {
  partial: "border-black/20 text-zinc-500",
  new: "border-champagne/60 text-champagne",
  contacted: "border-black/30 text-zinc-600",
  booked: "border-emerald-500/60 text-emerald-600",
};

export default function AdminPage() {
  const [user, setUser] = useState(undefined);
  const [leads, setLeads] = useState([]);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const m = document.createElement("meta");
    m.name = "robots";
    m.content = "noindex,nofollow";
    document.head.appendChild(m);
    return () => m.remove();
  }, []);

  const fetchLeads = async () => {
    const { data } = await axios.get(`${API}/leads`, { withCredentials: true });
    setLeads(data);
  };

  useEffect(() => {
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then((res) => {
        setUser(res.data);
        return fetchLeads();
      })
      .catch(() => setUser(null));
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, loginForm, { withCredentials: true });
      setUser(data);
      await fetchLeads();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed");
    } finally {
      setBusy(false);
    }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    setUser(null);
    setLeads([]);
  };

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/leads/${id}`, { status }, { withCredentials: true });
      setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, status } : l)));
    } catch {
      toast.error("Could not update status");
    }
  };

  if (user === undefined) {
    return (
      <main className="min-h-screen bg-obsidian flex items-center justify-center">
        <span className="h-2 w-2 rounded-full bg-champagne animate-pulse-gold" />
      </main>
    );
  }

  if (user === null) {
    return (
      <main className="min-h-screen bg-obsidian text-pearl flex items-center justify-center px-6" data-testid="admin-login-page">
        <div className="w-full max-w-md border border-black/10 bg-coal p-10 shadow-sm">
          <Logo compact />
          <h1 className="mt-10 font-display text-3xl font-light">Admin sign in</h1>
          <p className="mt-2 text-sm text-zinc-500">Call requests and leak report leads live here.</p>
          <form onSubmit={login} className="mt-8 space-y-5" data-testid="admin-login-form">
            <div>
              <label htmlFor="admin-email" className="sr-only">Email</label>
              <input
                id="admin-email"
                data-testid="admin-email-input"
                type="email"
                required
                value={loginForm.email}
                onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                placeholder="Email"
                className="w-full bg-white border border-black/10 focus:border-champagne/70 px-5 py-4 text-base text-pearl placeholder:text-zinc-400 outline-none transition-colors"
              />
            </div>
            <div>
              <label htmlFor="admin-password" className="sr-only">Password</label>
              <input
                id="admin-password"
                data-testid="admin-password-input"
                type="password"
                required
                value={loginForm.password}
                onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                placeholder="Password"
                className="w-full bg-white border border-black/10 focus:border-champagne/70 px-5 py-4 text-base text-pearl placeholder:text-zinc-400 outline-none transition-colors"
              />
            </div>
            {error && (
              <p className="text-sm text-red-500" data-testid="admin-login-error">{error}</p>
            )}
            <button
              type="submit"
              disabled={busy}
              data-testid="admin-login-button"
              className="w-full rounded-full bg-champagne px-6 py-4 text-sm font-medium text-white hover:bg-champagne-soft disabled:opacity-50 transition-colors"
            >
              {busy ? "Signing in..." : "Sign in"}
            </button>
          </form>
          <a href="/" className="mt-6 inline-block text-xs text-zinc-400 hover:text-champagne transition-colors">
            Back to site
          </a>
        </div>
      </main>
    );
  }

  const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
  const thisWeek = leads.filter((l) => new Date(l.created_at).getTime() > weekAgo).length;
  const booked = leads.filter((l) => l.status === "booked").length;

  return (
    <main className="min-h-screen bg-obsidian text-pearl" data-testid="admin-dashboard">
      <header className="border-b border-black/10 bg-white/85 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-[72px] flex items-center justify-between">
          <Logo compact />
          <div className="flex items-center gap-6">
            <span className="hidden sm:block font-mono text-xs text-zinc-500">{user.email}</span>
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 rounded-full border border-black/15 px-5 py-2.5 text-sm text-zinc-600 hover:border-champagne/60 hover:text-champagne transition-colors"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-14">
        <p className="font-mono text-xs tracking-[0.35em] uppercase text-champagne mb-4">Back office</p>
        <h1 className="font-display text-4xl md:text-5xl font-light tracking-tight">
          Call <span className="italic text-champagne">requests.</span>
        </h1>

        <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-px bg-black/10 border border-black/10">
          {[
            { icon: Inbox, label: "Total requests", value: leads.length, testid: "stat-total" },
            { icon: CalendarClock, label: "This week", value: thisWeek, testid: "stat-week" },
            { icon: CheckCircle2, label: "Booked", value: booked, testid: "stat-booked" },
          ].map((s) => (
            <div key={s.label} className="bg-coal px-6 py-6 flex items-center gap-5" data-testid={s.testid}>
              <s.icon size={22} className="text-champagne" strokeWidth={1.5} />
              <div>
                <p className="font-display text-3xl text-pearl">{s.value}</p>
                <p className="text-xs text-zinc-500 mt-0.5">{s.label}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 border border-black/10 bg-coal overflow-x-auto shadow-sm">
          <table className="w-full min-w-[900px] text-left" data-testid="leads-table">
            <thead>
              <tr className="border-b border-black/10 bg-carbon font-mono text-[11px] tracking-widest uppercase text-zinc-500">
                <th className="px-6 py-4 font-normal">Received</th>
                <th className="px-6 py-4 font-normal">Name</th>
                <th className="px-6 py-4 font-normal">Practice</th>
                <th className="px-6 py-4 font-normal">Contact</th>
                <th className="px-6 py-4 font-normal">Source</th>
                <th className="px-6 py-4 font-normal">Note</th>
                <th className="px-6 py-4 font-normal">Status</th>
              </tr>
            </thead>
            <tbody>
              {leads.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-6 py-16 text-center text-sm text-zinc-400" data-testid="leads-empty">
                    No requests yet. New submissions will appear here.
                  </td>
                </tr>
              )}
              {leads.map((l) => (
                <tr key={l.id} className="border-b border-black/5 hover:bg-champagne/[0.03] transition-colors" data-testid={`lead-row-${l.id}`}>
                  <td className="px-6 py-4 font-mono text-xs text-zinc-500 whitespace-nowrap">
                    {new Date(l.created_at).toLocaleString(undefined, {
                      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
                    })}
                  </td>
                  <td className="px-6 py-4 text-sm text-pearl">{l.name}</td>
                  <td className="px-6 py-4 text-sm text-zinc-600">
                    <div>{l.practice || "-"}</div>
                    {l.monthly_inquiries && (
                      <div className="text-zinc-400 text-xs mt-0.5">{l.monthly_inquiries} inquiries/mo</div>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-zinc-600">
                    <div>{l.email}</div>
                    <div className="text-zinc-400 text-xs mt-0.5">{l.phone}</div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="font-mono text-[11px] px-2.5 py-1 border border-black/10 rounded-full text-zinc-500 whitespace-nowrap" title={l.utm_source ? `utm: ${l.utm_source}` : ""}>
                      {l.source || "form"}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-zinc-500 max-w-[220px]">
                    <span className="line-clamp-2">{l.message || "-"}</span>
                  </td>
                  <td className="px-6 py-4">
                    <select
                      value={l.status}
                      onChange={(e) => setStatus(l.id, e.target.value)}
                      data-testid={`lead-status-select-${l.id}`}
                      className={`bg-white border px-3 py-1.5 text-xs rounded-full outline-none cursor-pointer ${statusStyles[l.status] || statusStyles.partial}`}
                    >
                      {STATUSES.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </main>
  );
}
